import { Box, Flex, Spinner, VStack } from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { addMonths, endOfMonth, format } from 'date-fns'
import dynamic from 'next/dynamic'
import { FormProvider, useForm } from 'react-hook-form'
import { useMount } from 'react-use'
import { tax } from 'src/api'
import { schemaForType } from 'src/common/schemeForType'
import { SelectBoxChildOption } from 'src/components/common/SelectBox/CategorySelectBox'
import { useApiClient } from 'src/hooks/useApiClient'
import { AccountItem } from 'src/interfaces/api/GetAccountItems'
import { GetClientResponse } from 'src/interfaces/api/GetClients'
import { GetInvoiceByIdResponseInvoice } from 'src/interfaces/api/GetInvoice'
import { GetInvoiceCaluculatedAmountsPayload } from 'src/interfaces/api/GetInvoiceCaluclatedAmounts'
import { GetInvoiceDuplicateInvoice } from 'src/interfaces/api/GetInvoiceDuplicate'
import { PostClientsPayload } from 'src/interfaces/api/PostClients'
import { characterCount } from 'src/utils/StringUtil'
import { z } from 'zod'
import { InvoiceBasicInformation } from './InvoiceBasicInformation'
import { InvoiceClient } from './InvoiceClient'
import InvoiceDateInformation from './InvoiceDateInformation'
import { InvoicePreview } from './InvoicePreview'
import { InvoiceReceiptInformation } from './InvoiceReceiptInformation'
import {
  getUnitSettingFromLocalStorage,
  getWithholdingSettingFromLocalStorage
} from './InvoiceReceiptInformation/TaxSettingModal'
import { InvoiceRemarks } from './InvoiceRemarks'
import InvoiceUserInformation from './InvoiceUserInformation'

// フォントファイルを含み、サイズが大きいのでdynamicインポートしてフィアル分割を効かせる
export const InvoiceFooter = dynamic(
  // 未解決のTSエラーなので無視する
  // https://github.com/vercel/next.js/issues/35986
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('./InvoiceFooter').then((mod) => mod.InvoiceFooter),
  { loading: () => <Spinner />, ssr: false }
)

type FormTaxSetting = Pick<
  GetInvoiceCaluculatedAmountsPayload,
  'tax_display' | 'tax_rounding' | 'amount_rounding'
> & {
  withholding: '0' | '1'
  unit: '0' | '1'
}
export interface FormInvoiceItem {
  name: string
  quantity: number
  unit: string
  unit_price: number
  tax_id: number
  is_withheld: boolean
  account_item_id: number
  id?: number
}

// このページで使用する自社情報の型定義
export interface FormCompany {
  address: string
  department_name: string
  email: string
  logo_url: string
  registration_number: string
  seal_url: string
  tel: string
  user_name: string
  name: string
  zip_code: string
  transfer_account: string
}

export type InvoiceFormInputs = {
  client: Pick<
    PostClientsPayload,
    | 'name'
    | 'honorific'
    | 'department_name'
    | 'person_in_charge'
    | 'person_in_charge_honorific'
  > & { id?: number | null }
  company: FormCompany
  title: string
  amount: number
  payment_deadline: string
  billing_date: string
  sales_date: string
  invoice_number: string
  remarks: string
  items: FormInvoiceItem[]
  taxSettings: FormTaxSetting
}

export const invoiceSchema = schemaForType<InvoiceFormInputs>()(
  z.object({
    client: z.object({
      id: z.number().optional().nullable(),
      name: z.string().min(1, '必須です'),
      honorific: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      department_name: z.string().max(150, '150文字以内で入力してください'),
      person_in_charge: z.string().max(150, '150文字以内で入力してください'),
      person_in_charge_honorific: z.union([z.literal(1), z.literal(2)])
    }),
    company: z.object({
      address: z.string().max(150, '150文字以内で入力してください'),
      department_name: z.string().max(150, '150文字以内で入力してください'),
      email: z.string(),
      logo_url: z.string(),
      registration_number: z.string(),
      seal_url: z.string(),
      tel: z.string(),
      user_name: z
        .string()
        .min(1, '必須です')
        .max(150, '150文字以内で入力してください'),
      name: z.string().max(150, '150文字以内で入力してください'),
      zip_code: z.string(),
      transfer_account: z.string().min(1, '必須です')
    }),
    taxSettings: z.object({
      tax_display: z.union([z.literal(0), z.literal(1)]),
      tax_rounding: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      amount_rounding: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      withholding: z.union([z.literal('0'), z.literal('1')]),
      unit: z.union([z.literal('0'), z.literal('1')])
    }),
    title: z.string().max(150, '150文字以内で入力してください'),
    amount: z.number(),
    payment_deadline: z.string().min(1, '必須です'),
    billing_date: z.string().min(1, '必須です'),
    sales_date: z.string().min(1, '必須です'),
    invoice_number: z
      .string()
      .min(1, '必須です')
      .max(150, '150文字以内で入力してください'),
    remarks: z.string(),
    items: z.array(
      z.object({
        name: z
          .string()
          .min(1, '入力してください')
          .refine(
            (value) => characterCount(value) <= 44,
            '全角22文字以内で入力してください'
          ),
        quantity: z.number({
          required_error: '入力してください',
          invalid_type_error: '入力してください'
        }),
        unit: z.string(),
        unit_price: z.number(),
        tax_id: z.number(),
        is_withheld: z.boolean(),
        account_item_id: z.number(),
        id: z.number().optional()
      })
    )
  })
)

type Props = {
  /**
   * すでに存在する請求書を表示する場合はAPIから取得した請求書がこの値として渡される
   */
  invoiceById?: GetInvoiceByIdResponseInvoice
  duplicatedInvoice?: GetInvoiceDuplicateInvoice
  accountItems: AccountItem[]
  accountItemsForReactSelect: SelectBoxChildOption[]
  clients: GetClientResponse[]
  taxes: tax[]
}

export const InvoiceEditForm = ({
  invoiceById,
  accountItems,
  clients,
  taxes,
  duplicatedInvoice
}: Props) => {
  const invoiceInitialProps = {
    client: {
      name: '',
      honorific: 0 as const,
      department_name: '',
      person_in_charge: '',
      person_in_charge_honorific: 1 as const
    },
    company: {
      user_name: '',
      name: '',
      department_name: '',
      registration_number: '',
      zip_code: '',
      address: '',
      tel: '',
      email: '',
      logo_url: '',
      seal_url: '',
      transfer_account: ''
    },
    taxSettings: {
      tax_display: 0 as const,
      tax_rounding: 0 as const,
      amount_rounding: 0 as const,
      withholding: getWithholdingSettingFromLocalStorage() || '0',
      unit: getUnitSettingFromLocalStorage() || '0'
    },

    title: '',
    amount: 0,
    payment_deadline: format(
      endOfMonth(addMonths(new Date(), 1)),
      'yyyy-MM-dd'
    ),
    sales_date: format(new Date(), 'yyyy-MM-dd'),
    billing_date: format(new Date(), 'yyyy-MM-dd'),
    items: [
      {
        name: '',
        quantity: 1,
        unit: '',
        tax_id: taxes[0].id,
        unit_price: 0,
        is_withheld: false,
        account_item_id: accountItems[0].id,
        id: Date.now()
      }
    ],
    remarks: ''
  }
  const methods = useForm<InvoiceFormInputs>({
    defaultValues: invoiceInitialProps,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(invoiceSchema)
  })
  const { reset, getValues } = methods
  const { apiClient } = useApiClient()

  /**
   * 請求書編集画面を開いたときの初期化処理
   */
  const init = async () => {
    const values = getValues()
    const { company } = await apiClient.getCompany()

    // 新規作成のとき
    if (!invoiceById && !duplicatedInvoice) {
      const { number } = await apiClient.getInvoiceNumber()
      reset({
        ...values,
        invoice_number: number,
        company: company ?? values.company
      })
    } else if (duplicatedInvoice) {
      const client = clients.find((v) => v.id === duplicatedInvoice.client.id)
      // 複製したとき
      reset({
        ...values,
        invoice_number: duplicatedInvoice?.invoice_number,
        company: company,
        client: client ?? (values.client as unknown as GetClientResponse),
        taxSettings: {
          ...duplicatedInvoice?.setting,
          unit: duplicatedInvoice?.items.some((item) => item.unit !== '')
            ? '1'
            : '0',
          withholding: duplicatedInvoice?.items.some((item) => item.is_withheld)
            ? '1'
            : '0'
        },
        title: duplicatedInvoice.title,
        amount: duplicatedInvoice.amount,
        payment_deadline: format(
          new Date(duplicatedInvoice.payment_deadline),
          'yyyy-MM-dd'
        ),
        sales_date: format(
          new Date(duplicatedInvoice.sales_date),
          'yyyy-MM-dd'
        ),
        billing_date: format(
          new Date(duplicatedInvoice.billing_date),
          'yyyy-MM-dd'
        ),
        items:
          duplicatedInvoice.items && duplicatedInvoice.items.length > 0
            ? duplicatedInvoice.items
            : values.items,
        remarks: duplicatedInvoice.remarks
      })
    } else if (invoiceById) {
      // 既存の請求書を編集するとき
      const client = clients.find((v) => v.id === invoiceById.client.id)
      reset({
        ...values,
        client: client ?? (values.client as unknown as GetClientResponse),
        invoice_number: invoiceById?.invoice_number,
        company: company,
        taxSettings: {
          ...invoiceById.setting,
          unit: invoiceById.items.some((item) => item.unit !== '') ? '1' : '0',
          withholding: invoiceById?.items.some((item) => item.is_withheld)
            ? '1'
            : '0'
        },
        title: invoiceById.title,
        amount: invoiceById.amount,
        payment_deadline: format(
          new Date(invoiceById.payment_deadline),
          'yyyy-MM-dd'
        ),
        sales_date: format(new Date(invoiceById.sales_date), 'yyyy-MM-dd'),
        billing_date: format(new Date(invoiceById.billing_date), 'yyyy-MM-dd'),
        items: [...invoiceById.items],
        remarks: invoiceById.remarks
      })
    }
  }
  useMount(() => {
    init()
  })

  return (
    <div id="all">
      <FormProvider {...methods}>
        <Flex width={'100%'}>
          <InvoicePreview width="100%" height="auto" />

          <Box
            w="100%"
            h="calc(100vh - 64px)"
            overflowY={'scroll'}
            backgroundColor={'white'}
            sx={{
              scrollbarWidth: 'none',
              '-ms-overflow-style': 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            <VStack alignItems={'flex-start'} px="16px" py="4px">
              <InvoiceClient />
              <InvoiceBasicInformation />
              <InvoiceDateInformation />
              <InvoiceReceiptInformation
                accountItems={accountItems}
                taxes={taxes}
              />
              <InvoiceUserInformation />
              <InvoiceRemarks />
            </VStack>
            {/* 未解決のTSエラーなので無視する */}
            {/* https://github.com/vercel/next.js/issues/35986 */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <InvoiceFooter
              invoiceById={invoiceById}
              duplicatedInvoice={duplicatedInvoice}
            />
          </Box>
        </Flex>
      </FormProvider>
    </div>
  )
}
